import React from 'react';
import { Link } from 'react-router-dom';
import { clientBenefits } from '../mockData';
import { MessageSquare, CheckCircle, Zap, DollarSign, GitBranch, Shield, Award, Users } from 'lucide-react';
import { Button } from '../components/ui/button';

const iconMap = {
  MessageSquare: MessageSquare,
  CheckCircle: CheckCircle,
  Zap: Zap,
  DollarSign: DollarSign,
  GitBranch: GitBranch
};

const ForClients = () => {
  return (
    <div style={{ backgroundColor: '#1a1c1b', minHeight: '100vh' }}>
      {/* Hero Section */}
      <section style={{
        position: 'relative',
        padding: '120px 40px 80px',
        backgroundColor: '#302f2c',
        borderBottom: '1px solid rgba(63, 72, 22, 0.5)',
        overflow: 'hidden'
      }}>
        <div style={{
          maxWidth: '87.5rem',
          margin: '0 auto',
          textAlign: 'center',
          position: 'relative',
          zIndex: 1
        }}>
          <h1 style={{
            fontFamily: 'Arial, sans-serif',
            fontWeight: 900,
            fontSize: 'clamp(2.5rem, 5vw, 4rem)',
            lineHeight: 0.95,
            color: '#d9fb06',
            textTransform: 'uppercase',
            marginBottom: '24px'
          }}>
            For Clients
          </h1>
          <p style={{
            fontFamily: 'Inter, Arial, sans-serif',
            fontSize: '1.5rem',
            lineHeight: 1.5,
            color: '#dfddd6',
            maxWidth: '50rem',
            margin: '0 auto 32px'
          }}>
            Premium Workforce Delivery for Complex Projects
          </p>
          <p style={{
            fontFamily: 'Inter, Arial, sans-serif',
            fontSize: '1.1rem',
            lineHeight: 1.6,
            color: '#888680',
            maxWidth: '45rem',
            margin: '0 auto'
          }}>
            Worklink Global simplifies workforce sourcing by acting as your central coordination partner.
          </p>
        </div>
      </section>

      {/* Client Benefits Grid */}
      <section style={{
        padding: '96px 40px',
        backgroundColor: '#1a1c1b'
      }}>
        <div style={{
          maxWidth: '87.5rem',
          margin: '0 auto'
        }}>
          <h2 style={{
            fontFamily: 'Arial, sans-serif',
            fontWeight: 900,
            fontSize: 'clamp(2rem, 4vw, 3rem)',
            lineHeight: 1.1,
            color: '#d9fb06',
            textTransform: 'uppercase',
            marginBottom: '48px',
            textAlign: 'center'
          }}>
            What You Receive
          </h2>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '32px'
          }}>
            {clientBenefits.map((benefit, index) => {
              const IconComponent = iconMap[benefit.icon];
              return (
                <div
                  key={index}
                  style={{
                    backgroundColor: '#302f2c',
                    padding: '40px',
                    borderRadius: '8px',
                    border: '1px solid rgba(63, 72, 22, 0.5)',
                    transition: 'transform 0.3s ease, background-color 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.backgroundColor = '#3f4816';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.backgroundColor = '#302f2c';
                  }}
                >
                  <IconComponent size={40} style={{ color: '#d9fb06', marginBottom: '20px' }} />
                  <h3 style={{
                    fontFamily: 'Arial, sans-serif',
                    fontWeight: 700,
                    fontSize: '1.3rem',
                    color: '#d9fb06',
                    marginBottom: '16px',
                    textTransform: 'uppercase'
                  }}>
                    {benefit.title}
                  </h3>
                  <p style={{
                    fontFamily: 'Inter, Arial, sans-serif',
                    fontSize: '1rem',
                    lineHeight: 1.6,
                    color: '#dfddd6'
                  }}>
                    {benefit.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Our Commitment */}
      <section style={{
        padding: '96px 40px',
        backgroundColor: '#302f2c'
      }}>
        <div style={{
          maxWidth: '87.5rem',
          margin: '0 auto'
        }}>
          <h2 style={{
            fontFamily: 'Arial, sans-serif',
            fontWeight: 900,
            fontSize: 'clamp(2rem, 4vw, 3rem)',
            lineHeight: 1.1,
            color: '#d9fb06',
            textTransform: 'uppercase',
            marginBottom: '48px',
            textAlign: 'center'
          }}>
            Our Commitment to Clients
          </h2>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '24px'
          }}>
            {[
              { icon: Award, text: 'Quality workforce allocation' },
              { icon: Shield, text: 'Zero shortcuts on safety' },
              { icon: CheckCircle, text: 'Full transparency' },
              { icon: Users, text: 'Strict anti-poaching protections' },
              { icon: Zap, text: 'Reliable service continuity' },
              { icon: GitBranch, text: 'Consistent delivery regardless of market shortages' }
            ].map((item, index) => {
              const IconComponent = item.icon;
              return (
                <div
                  key={index}
                  style={{
                    backgroundColor: '#1a1c1b',
                    padding: '24px',
                    borderRadius: '8px',
                    border: '1px solid rgba(63, 72, 22, 0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '16px',
                    transition: 'background-color 0.3s ease'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#3f4816'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#1a1c1b'}
                >
                  <IconComponent size={28} style={{ color: '#d9fb06', flexShrink: 0 }} />
                  <p style={{
                    fontFamily: 'Inter, Arial, sans-serif',
                    fontSize: '1rem',
                    color: '#dfddd6',
                    margin: 0
                  }}>
                    {item.text}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Image Section */}
      <section style={{
        padding: '96px 40px',
        backgroundColor: '#1a1c1b'
      }}>
        <div style={{
          maxWidth: '87.5rem',
          margin: '0 auto',
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: '40px',
          alignItems: 'center'
        }}>
          <div>
            <h2 style={{
              fontFamily: 'Arial, sans-serif',
              fontWeight: 900,
              fontSize: 'clamp(1.8rem, 3vw, 2.5rem)',
              lineHeight: 1.1,
              color: '#d9fb06',
              textTransform: 'uppercase',
              marginBottom: '24px'
            }}>
              Confidence, Stability, and Performance
          </h2>
            <p style={{
              fontFamily: 'Inter, Arial, sans-serif',
              fontSize: '1.1rem',
              lineHeight: 1.7,
              color: '#dfddd6',
              marginBottom: '24px'
            }}>
              We deliver exceptional workforce solutions through our trusted aggregator model, ensuring your projects receive the skilled professionals they need, when they need them.
            </p>
            <p style={{
              fontFamily: 'Inter, Arial, sans-serif',
              fontSize: '1rem',
              lineHeight: 1.6,
              color: '#888680'
            }}>
              Our multi-supplier network provides backup guarantees, compliance assurance, and performance consistency that single-source providers simply cannot match.
            </p>
          </div>
          <div style={{
            borderRadius: '8px',
            overflow: 'hidden',
            border: '1px solid rgba(63, 72, 22, 0.5)'
          }}>
            <img
              src="https://images.unsplash.com/photo-1709715357520-5e1047a2b691?w=800&q=80"
              alt="Professional team collaboration"
              style={{
                width: '100%',
                height: '400px',
                objectFit: 'cover'
              }}
            />
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section style={{
        padding: '96px 40px',
        backgroundColor: '#3f4816',
        textAlign: 'center'
      }}>
        <div style={{
          maxWidth: '60rem',
          margin: '0 auto'
        }}>
          <h2 style={{
            fontFamily: 'Arial, sans-serif',
            fontWeight: 900,
            fontSize: 'clamp(2rem, 4vw, 3rem)',
            lineHeight: 1.1,
            color: '#d9fb06',
            marginBottom: '24px',
            textTransform: 'uppercase'
          }}>
            Ready to Experience the Difference?
          </h2>
          <p style={{
            fontFamily: 'Inter, Arial, sans-serif',
            fontSize: '1.25rem',
            lineHeight: 1.6,
            color: '#dfddd6',
            marginBottom: '40px'
          }}>
            Let's discuss your workforce requirements and project needs
          </p>
          <Link to="/contact?form=workforce" style={{ textDecoration: 'none' }}>
            <Button
              style={{
                background: '#d9fb06',
                color: '#1a1c1b',
                border: 'none',
                borderRadius: '10rem',
                padding: '1.2em 2em',
                fontFamily: 'Inter, Arial, sans-serif',
                fontWeight: 600,
                fontSize: '1rem',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                textTransform: 'uppercase'
              }}
              onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.02)'}
              onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
            >
              Request Workforce Today
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ForClients;
